import type { PerfectDrawProject } from "../projects/types";
import { migrateProject } from "../projects/projectMigration";

export const DATABASE_NAME = "perfect-draw";
export const DATABASE_VERSION = 2;
export const PROJECT_STORE_NAME = "projects";
export const ARTWORK_STORE_NAME = "artworks";
export const DATABASE_OPEN_TIMEOUT_MS = 10_000;

export class DatabaseUpgradeBlockedError extends Error {
  public readonly code = "DATABASE_UPGRADE_BLOCKED";
  public readonly retryable = true;

  public constructor() {
    super("Another PERFECT DRAW tab is preventing the storage upgrade. Close it and retry.");
    this.name = "DatabaseUpgradeBlockedError";
  }
}

export class DatabaseOpenTimeoutError extends Error {
  public readonly code = "DATABASE_OPEN_TIMEOUT";
  public readonly retryable = true;

  public constructor() {
    super("PERFECT DRAW storage did not open in time. Please retry.");
    this.name = "DatabaseOpenTimeoutError";
  }
}

interface DatabaseOpenLifecycleOptions {
  timeoutMs?: number;
}

export function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB request failed")),
      {
        once: true,
      },
    );
  });
}

export function transactionToPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("IndexedDB transaction was aborted")),
      { once: true },
    );
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("IndexedDB transaction failed")),
      { once: true },
    );
  });
}

export function waitForDatabaseOpen(
  request: IDBOpenDBRequest,
  options: DatabaseOpenLifecycleOptions = {},
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new DatabaseOpenTimeoutError());
    }, options.timeoutMs ?? DATABASE_OPEN_TIMEOUT_MS);

    const clearOpenTimeout = (): void => clearTimeout(timeout);
    request.addEventListener(
      "blocked",
      () => {
        if (settled) return;
        settled = true;
        clearOpenTimeout();
        reject(new DatabaseUpgradeBlockedError());
      },
      { once: true },
    );
    request.addEventListener(
      "error",
      () => {
        if (settled) return;
        settled = true;
        clearOpenTimeout();
        reject(request.error ?? new Error("IndexedDB open failed"));
      },
      { once: true },
    );
    request.addEventListener(
      "success",
      () => {
        const database = request.result;
        if (settled) {
          // An IndexedDB open request cannot be cancelled. If a blocker goes
          // away after the caller already received a failure, close the late
          // connection immediately so it cannot leak or block future upgrades.
          database.close();
          return;
        }
        settled = true;
        clearOpenTimeout();
        database.addEventListener("versionchange", () => database.close(), { once: true });
        resolve(database);
      },
      { once: true },
    );
  });
}

export async function openDatabase(): Promise<IDBDatabase> {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);

  request.addEventListener("upgradeneeded", () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(PROJECT_STORE_NAME)) {
      const store = database.createObjectStore(PROJECT_STORE_NAME, { keyPath: "id" });
      store.createIndex("updatedAt", "updatedAt", { unique: false });
    }
    if (!database.objectStoreNames.contains(ARTWORK_STORE_NAME)) {
      database.createObjectStore(ARTWORK_STORE_NAME, { keyPath: "projectId" });
    }
  });

  return waitForDatabaseOpen(request);
}

export async function saveProject(project: PerfectDrawProject): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(PROJECT_STORE_NAME, "readwrite");
    transaction.objectStore(PROJECT_STORE_NAME).put(migrateProject(project).project);
    await transactionToPromise(transaction);
  } finally {
    database.close();
  }
}

export async function getProject(projectId: string): Promise<PerfectDrawProject | undefined> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(PROJECT_STORE_NAME, "readonly");
    const request = transaction.objectStore(PROJECT_STORE_NAME).get(projectId);
    const result = (await requestToPromise(request)) as PerfectDrawProject | undefined;
    await transactionToPromise(transaction);
    return result ? migrateProject(result).project : undefined;
  } finally {
    database.close();
  }
}

export async function listProjects(): Promise<PerfectDrawProject[]> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(PROJECT_STORE_NAME, "readonly");
    const request = transaction.objectStore(PROJECT_STORE_NAME).getAll();
    const projects = (await requestToPromise(request)) as PerfectDrawProject[];
    await transactionToPromise(transaction);
    return projects
      .map((project) => migrateProject(project).project)
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
  } finally {
    database.close();
  }
}

export async function deleteProject(projectId: string): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction([PROJECT_STORE_NAME, ARTWORK_STORE_NAME], "readwrite");
    transaction.objectStore(PROJECT_STORE_NAME).delete(projectId);
    transaction.objectStore(ARTWORK_STORE_NAME).delete(projectId);
    await transactionToPromise(transaction);
  } finally {
    database.close();
  }
}

export function deleteDatabase(): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME);
    request.addEventListener("success", () => resolve(), { once: true });
    request.addEventListener("blocked", () => reject(new Error("Database deletion was blocked")), {
      once: true,
    });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("Database deletion failed")),
      {
        once: true,
      },
    );
  });
}
